var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import useModal from '@delangle/use-modal';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { buildUseOnlyOneInstanceOpened } from '../_internal/buildUseOnlyOneInstanceOpened';
import { isClientSide } from '../_internal/ssr';
import { Text } from '../Text';
import { useThemeVariant } from '../useThemeVariant';
import { useTooltip } from './Tooltip.hooks';
import { TooltipVisibilityState } from './Tooltip.interface';
import { ANIMATION_DURATION, TooltipContainer } from './Tooltip.style';
export var useOnlyOneInstanceOpened = buildUseOnlyOneInstanceOpened().useOnlyOneInstanceOpened;
export var Tooltip = React.forwardRef(function (props, ref) {
    var title = props.title, description = props.description, children = props.children, _a = props.small, small = _a === void 0 ? false : _a, onClick = props.onClick, _b = props.backgroundStyle, backgroundStyle = _b === void 0 ? 'dark' : _b, triggerRef = props.triggerRef, rest = __rest(props, ["title", "description", "children", "small", "onClick", "backgroundStyle", "triggerRef"]);
    var _c = useTooltip(props, ref, triggerRef), state = _c[0], actions = _c[1], refs = _c[2];
    var theme = useThemeVariant();
    var isOpen = state.visibilityState === TooltipVisibilityState.Visible;
    var modal = useModal({
        open: isOpen,
        onClose: function () { },
        persistent: true,
        animated: true,
        animationDuration: ANIMATION_DURATION,
    });
    useOnlyOneInstanceOpened({ open: isOpen, onClose: actions.onMouseLeave });
    var content = React.isValidElement(children)
        ? React.cloneElement(children, __assign({ ref: refs.trigger, onMouseEnter: actions.onMouseEnter, onMouseLeave: actions.onMouseLeave }, (onClick ? { onClick: onClick } : {})))
        : children;
    /**
     * Needed to prevent onMouseLeave breaking cases like scroll or resizes
     * Checks if the mouse is moving inside of the trigger element every 500ms
     */
    React.useEffect(function () {
        var _a;
        if (refs.trigger && isOpen) {
            var node_1 = (_a = refs.trigger) === null || _a === void 0 ? void 0 : _a.current;
            var onMouseLeave_1 = actions.onMouseLeave;
            if (node_1) {
                var timeoutRef_1;
                var onDocumentMouseMove_1 = function (e) {
                    if (!node_1.contains(e.target)) {
                        onMouseLeave_1();
                    }
                    else {
                        timeoutRef_1 = addAsyncMouseListener_1();
                    }
                };
                var addAsyncMouseListener_1 = function () {
                    return window.setTimeout(function () {
                        return document.addEventListener('mousemove', onDocumentMouseMove_1, {
                            once: true,
                        });
                    }, 500);
                };
                timeoutRef_1 = addAsyncMouseListener_1();
                return function () { return clearTimeout(timeoutRef_1); };
            }
        }
    }, [actions.onMouseLeave, isOpen, refs.trigger]);
    React.useEffect(function () {
        if (triggerRef) {
            var node_2 = triggerRef.current;
            if (node_2) {
                node_2.addEventListener('mouseover', actions.onMouseEnter);
                node_2.addEventListener('mouseout', actions.onMouseLeave);
                return function () {
                    node_2.removeEventListener('mouseover', actions.onMouseEnter);
                    node_2.removeEventListener('mouseout', actions.onMouseLeave);
                };
            }
        }
    }, [actions.onMouseEnter, actions.onMouseLeave, triggerRef]);
    return (React.createElement(React.Fragment, null,
        content,
        isClientSide &&
            state.visibilityState !== TooltipVisibilityState.NotInstantiated &&
            ReactDOM.createPortal(React.createElement(TooltipContainer, __assign({ ref: refs.tooltip, backgroundColor: backgroundStyle === 'light'
                    ? theme.neutralColor.withIntensityFading[100]
                    : theme.neutralColor.withIntensityFading[700], "data-has-description": !!description, "data-state": modal.state, style: state.position }, rest),
                React.createElement(Text, { variation: "title", type: small ? 'caption' : 'regular' }, title),
                description && (React.createElement(Text, { type: small ? 'caption' : 'regular' }, description))), document.body)));
});
//# sourceMappingURL=Tooltip.js.map